import { useMutation, useQueryClient } from "@tanstack/react-query";
import { executeGraphQLMutation } from "@/utils/helper/graphql-helper";
import { createCartItems, updateCartItems, deleteCartItems } from "@/graphql/mutations";
import { Cart } from "@/types/AppTypes";
import { toast } from "sonner";
import { parseJSONField } from "@/utils/helper/parseJSON";

// Create Cart Item Mutation with duplicate checking using cache
export const useCreateCartItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (cartItem: {
      user_id: string;
      product_id: string;
      quantity: number;
      variant?: any;
    }) => {
      // Check existing items in React Query cache instead of making a new GraphQL call
      const existingCartData = queryClient.getQueryData([
        "all_cart_items", 
        { user_id: { eq: cartItem?.user_id } }
      ]) as Cart[] || [];

      // Find existing item with same product_id and variant
      const existingItem = existingCartData.find((item: any) => {
        const itemVariant = item?.variant ? parseJSONField(item?.variant) : null;
        const newItemVariant = cartItem?.variant ? parseJSONField(cartItem?.variant) : null;
        
        return item?.product_id === cartItem?.product_id && 
               (!newItemVariant?.id || itemVariant?.id === newItemVariant?.id);
      }) as any;
      
      if (existingItem) {
        // Item already exists, update the quantity
        const newQuantity = (existingItem.quantity || 1) + cartItem.quantity;
        
        const response = await executeGraphQLMutation({
          query: updateCartItems,
          variables: {
            input: {
              id: existingItem.id,
              quantity: newQuantity
            }
          }
        });
        
        return {
          ...response?.data?.updateCartItems,
          wasUpdated: true
        };
      } else {
        // Item doesn't exist, create new cart item
        const response = await executeGraphQLMutation({
          query: createCartItems,
          variables: {
            input: cartItem
          }
        });
        
        return {
          ...response?.data?.createCartItems,
          wasUpdated: false
        };
      }
    },
    onSuccess: (data, variables) => {
      // Only invalidate if the user is authenticated
      if (variables.user_id) {
        queryClient.invalidateQueries({
          queryKey: ["all_cart_items", { user_id: { eq: variables.user_id } }]
        });
      }
      
      if (data.wasUpdated) {
        toast.success("Cart item quantity updated");
      } else {
        toast.success("Item added to cart");
      }
    },
    onError: (error) => {
      console.error("Error creating/updating cart item:", error);
      toast.error("Failed to add item to cart");
    }
  });
};

// Update Cart Item Mutation
export const useUpdateCartItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updateData }: {
      id: string;
      quantity?: number;
      variant?: any;
    }) => {
      const response = await executeGraphQLMutation({
        query: updateCartItems,
        variables: {
          input: {
            id,
            ...updateData
          }
        }
      });
      return response?.data?.updateCartItems;
    },
    onSuccess: (data, variables) => {
      // Invalidate cart queries to refetch updated data
      queryClient.invalidateQueries({
        queryKey: ["all_cart_items"]
      });
      toast.success("Cart updated");
    },
    onError: (error) => {
      console.error("Error updating cart item:", error);
      toast.error("Failed to update cart");
    }
  });
};

// Delete Cart Item Mutation
export const useDeleteCartItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await executeGraphQLMutation({
        query: deleteCartItems,
        variables: {
          input: { id }
        }
      });
      return response?.data?.deleteCartItems;
    },
    onSuccess: (data, variables) => {
      // Invalidate cart queries to refetch updated data
      queryClient.invalidateQueries({
        queryKey: ["all_cart_items"]
      });
      toast.success("Item removed from cart");
    },
    onError: (error) => {
      console.error("Error deleting cart item:", error);
      toast.error("Failed to remove item from cart");
    }
  });
};

// Bulk Delete Cart Items Mutation
export const useBulkDeleteCartItems = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ids: string[]) => {
      const deletePromises = ids.map(id =>
        executeGraphQLMutation({
          query: deleteCartItems,
          variables: {
            input: { id }
          }
        })
      );
      
      const responses = await Promise.all(deletePromises);
      return responses.map(response => response?.data?.deleteCartItems);
    },
    onSuccess: (data, variables) => {
      // Invalidate cart queries to refetch updated data
      queryClient.invalidateQueries({
        queryKey: ["all_cart_items"]
      });
      toast.success("Cart cleared");
    },
    onError: (error) => {
      console.error("Error clearing cart:", error);
      toast.error("Failed to clear cart");
    }
  });
}; 